import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, Calendar, ShoppingBag } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { getLocalizedField, pickLocalizedText } from '../utils/localizedContent';

const BANNERS = [
  {
    id: 1,
    type: 'promo',
    title: 'Nuevos Lanzamientos TCG',
    title_en: 'New TCG Releases',
    subtitle: 'Descubre las últimas expansiones de Pokémon, Magic y más',
    subtitle_en: 'Discover the latest expansions from Pokémon, Magic and more',
    cta: 'Ver Tienda',
    cta_en: 'Shop Now',
    link: '/store?category=tcg',
    image: 'https://images.unsplash.com/photo-1606503153255-59d8b8b82176?w=1200&h=400&fit=crop',
    gradient: 'from-[#0D5E4A]/90 via-[#0A1612]/70 to-transparent',
    accent: '#C9A962'
  },
  {
    id: 2,
    type: 'event',
    title: 'Torneo Pokémon TCG',
    title_en: 'Pokémon TCG Tournament',
    subtitle: 'Cada sábado a las 16:00. ¡Premios increíbles!',
    subtitle_en: 'Every Saturday at 4 PM. Amazing prizes!',
    cta: 'Ver Eventos',
    cta_en: 'View Events',
    link: '/events',
    image: 'https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?w=1200&h=400&fit=crop',
    gradient: 'from-[#C9A962]/80 via-[#0A1612]/70 to-transparent',
    accent: '#D4A84B'
  },
  {
    id: 3,
    type: 'reservation',
    title: 'Reserva Tu Mesa',
    title_en: 'Book Your Table',
    subtitle: 'Ven con amigos y disfruta de nuestros juegos. Mesas para 2-10 personas.',
    subtitle_en: 'Come with friends and enjoy our games. Tables for 2-10 people.',
    cta: 'Reservar Ahora',
    cta_en: 'Book Now',
    link: '/reservations/tables',
    image: 'https://images.unsplash.com/photo-1632501641765-e568d28b0015?w=1200&h=400&fit=crop',
    gradient: 'from-[#2D8B70]/90 via-[#0A1612]/70 to-transparent',
    accent: '#2D8B70'
  },
  {
    id: 4,
    type: 'collection',
    title: 'Gestiona tu Colección',
    title_en: 'Manage Your Collection',
    subtitle: 'Registra y organiza todas tus cartas TCG en un solo lugar',
    subtitle_en: 'Register and organize all your TCG cards in one place',
    cta: 'Crear Colección',
    cta_en: 'Create Collection',
    link: '/collections',
    image: 'https://images.unsplash.com/photo-1611371805429-8b5c1b2c34ba?w=1200&h=400&fit=crop',
    gradient: 'from-[#8B7355]/90 via-[#0A1612]/70 to-transparent',
    accent: '#C9A962'
  }
];

export default function FeaturedCarousel() {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const { lang } = useLang();

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % BANNERS.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + BANNERS.length) % BANNERS.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [isPaused, next]);

  const banner = BANNERS[current];

  const getIcon = (type) => {
    switch (type) {
      case 'promo': return <ShoppingBag size={16} />;
      case 'event': return <Calendar size={16} />;
      default: return <Sparkles size={16} />;
    }
  };

  return (
    <section 
      className="relative w-full h-[280px] sm:h-[320px] overflow-hidden rounded-2xl"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="featured-carousel"
    >
      {/* Background Image */}
      <div className="absolute inset-0 transition-all duration-700">
        <img
          src={banner.image}
          alt={getLocalizedField(banner, 'title', lang, banner.title)}
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${banner.gradient}`}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1612]/90 via-transparent to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
        <div className="max-w-lg">
          <div 
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3"
            style={{ backgroundColor: `${banner.accent}20`, color: banner.accent, border: `1px solid ${banner.accent}40` }}
          >
            {getIcon(banner.type)}
            {banner.type === 'promo'
              ? pickLocalizedText(lang, 'Destacado', 'Featured')
              : banner.type === 'event'
                ? pickLocalizedText(lang, 'Evento', 'Event')
                : pickLocalizedText(lang, 'Novedad', 'New')}
          </div>
          <h2 
            className="text-2xl sm:text-3xl font-bold text-[#F5F0E6] mb-2 leading-tight"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            {getLocalizedField(banner, 'title', lang, banner.title)}
          </h2>
          <p className="text-[#A8B5AE] text-sm sm:text-base mb-4 line-clamp-2">
            {getLocalizedField(banner, 'subtitle', lang, banner.subtitle)}
          </p>
          <Link 
            to={banner.link}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-105"
            style={{ backgroundColor: banner.accent, color: '#0A1612' }}
            data-testid={`carousel-cta-${banner.id}`}
          >
            {getLocalizedField(banner, 'cta', lang, banner.cta)}
            <ChevronRight size={16} />
          </Link>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prev}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#0A1612]/60 backdrop-blur-sm border border-[#C9A962]/20 text-[#F5F0E6] flex items-center justify-center hover:bg-[#0A1612]/80 transition-all z-20"
        data-testid="carousel-prev"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={next}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#0A1612]/60 backdrop-blur-sm border border-[#C9A962]/20 text-[#F5F0E6] flex items-center justify-center hover:bg-[#0A1612]/80 transition-all z-20"
        data-testid="carousel-next"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {BANNERS.map((banner, i) => (
          <button
            key={`carousel-dot-${banner.id || i}`}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-[#C9A962] w-6' : 'bg-[#F5F0E6]/30 hover:bg-[#F5F0E6]/50'}`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}
